import { Link } from 'react-router-dom';
import type { Story } from '@/mocks/stories';
import Tag from '@/components/base/Tag';

type StoryCardProps = {
  story: Story;
  variant?: 'default' | 'row';
};

const formatHeat = (value: number) =>
  value >= 10000 ? `${(value / 10000).toFixed(1)} 万` : value.toLocaleString('zh-CN');

const statusTone = (status: Story['status']) => {
  if (status === '追踪中') return 'primary' as const;
  if (status === '待核实') return 'accent' as const;
  return 'secondary' as const;
};

const StoryCard = ({ story, variant = 'default' }: StoryCardProps) => {
  if (variant === 'row') {
    return (
      <Link
        to={`/melon/${story.id}`}
        className="group flex gap-4 border-b border-background-200 py-4 transition-colors hover:bg-background-100"
      >
        <div className="h-20 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={story.cover}
            alt={`${story.title} 配图`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2 text-xs text-foreground-500">
            <span className="text-primary-700 font-medium">{story.category}</span>
            <span>·</span>
            <span>{story.date}</span>
          </div>
          <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700">
            {story.title}
          </h3>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-600" />
              {formatHeat(story.heat)}
            </span>
            <span>{story.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/melon/${story.id}`} className="block aspect-[4/3] overflow-hidden bg-background-200">
        <img
          src={story.cover}
          alt={`${story.title} 配图`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Tag tone={statusTone(story.status)}>
            <i className="ri-radar-line" />
            {story.status}
          </Tag>
          <Tag tone="neutral">{story.category}</Tag>
        </div>
        <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950">
          <Link to={`/melon/${story.id}`} className="hover:text-primary-700">
            {story.title}
          </Link>
        </h3>
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-600">{story.excerpt}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" />
            {story.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-600" />
              {formatHeat(story.heat)}
            </span>
            <span>{story.date}</span>
          </span>
        </div>
      </div>
    </article>
  );
};

export default StoryCard;